import React, { useState, useEffect } from "react";
import { GetData, PostData, PutData, DeleteData } from "../../services/api";
import DataTable from "react-data-table-component";
import moment from "moment";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
  Container,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Col,
  Button,
  Label,
  FormGroup,
} from "reactstrap";
import { Link, Navigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import Swal from 'sweetalert2';
import ReactPaginate from 'react-paginate';

const SalesTeams = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignmentStrategy, setAssignmentStrategy] = useState("round-robin");
  const [isActive, setIsActive] = useState(true);

  // Check if user is admin
  const getCurrentUserRole = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const user = JSON.parse(authUser);
        // El rol puede estar en el JWT decodificado o necesitamos obtenerlo del perfil
        return user.role || user.name?.role || "user";
      }
    } catch (error) {
      console.error("Error getting user role:", error);
    }
    return "user";
  };

  const userRole = getCurrentUserRole();
  const isAdmin = userRole === "admin";
  
  // Debug: log para verificar el rol
  console.log("Current user role:", userRole, "isAdmin:", isAdmin);

  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 10;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = data.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(data.length / itemsPerPage);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % data.length;
    setItemOffset(newOffset);
  };

  const fetchData = async () => {
    try {
      const { data } = await GetData("/sales-teams");
      const ordered = data.sort((a, b) => { 
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0); 
      });
      setData(ordered);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sales teams:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Nombre</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estrategia</span>,
      selector: (row) => row.assignmentStrategy === 'percentage' ? 'Por Porcentajes' : 'Round Robin',
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Miembros</span>,
      selector: (row) => row.members?.length || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      selector: (row) => row.isActive ? 'Activo' : 'Inactivo',
      sortable: true,
      cell: (row) => (
        <span className={`badge ${row.isActive ? 'bg-success' : 'bg-danger'}`}>
          {row.isActive ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Creado</span>,
      selector: (row) => row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY HH:mm") : "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Acción</span>,
      sortable: false,
      cell: (data) => {
        return (
          <UncontrolledDropdown className="dropdown d-inline-block" direction="down">
            <DropdownToggle
              className="btn btn-soft-secondary btn-sm"
              tag="button"
              type="button"
            >
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu 
              className="dropdown-menu-end" 
              style={{ zIndex: 9999 }}
              flip={false}
            >
              <DropdownItem>
                <Link to={`/sales-teams/${data.id}`} className="text-muted">
                  <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                  Ver Detalles
                </Link>
              </DropdownItem>
              <DropdownItem 
                className="edit-item-btn" 
                onClick={() => {
                  setEditingTeam(data);
                  setName(data.name);
                  setDescription(data.description || "");
                  setAssignmentStrategy(data.assignmentStrategy || "round-robin");
                  setIsActive(data.isActive !== false);
                  setEditModal(true);
                }}
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                Editar
              </DropdownItem>
              <DropdownItem 
                className="remove-item-btn" 
                onClick={() => deleteTeam(data.id)}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                Eliminar
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const createTeam = async () => {
    try {
      const { status } = await PostData("/sales-teams", { 
        name,
        description,
        assignmentStrategy,
        isActive
      });
      if (status === 200) {
        Swal.fire({
          title: `Equipo "${name}" creado`,
          icon: "success"
        });
        fetchData();
        setModal(false);
        resetForm();
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Error",
        text: err.response?.data?.error?.message || err.message || "Error al crear el equipo",
        icon: "error"
      });
    }
  };

  const updateTeam = async () => {
    try {
      const response = await PutData(`/sales-teams/${editingTeam.id}`, { 
        name,
        description,
        assignmentStrategy,
        isActive
      });
      if (response?.status === 204 || response?.status === 200) {
        Swal.fire({
          title: `Equipo "${name}" actualizado`,
          icon: "success"
        });
        fetchData();
        setEditModal(false);
        setEditingTeam(null);
        resetForm();
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Error",
        text: err.response?.data?.error?.message || err.message || "Error al actualizar el equipo",
        icon: "error"
      });
    }
  };

  const deleteTeam = (id) => {
    Swal.fire({
      title: "¿Deseas eliminar el equipo?",
      text: "Esta acción no se puede deshacer",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#dc3545'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DeleteData(`/sales-teams/${id}`);
          Swal.fire("Equipo eliminado!", "", "success");
          fetchData();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err.response?.data?.error?.message || err.message || "Error al eliminar el equipo",
            icon: "error"
          });
        }
      }
    });
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setAssignmentStrategy("round-robin");
    setIsActive(true);
  };

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title="Ventas" breadcrumbItem="Equipos de Venta" />
        <Row className="my-4">
          <Col className="col-sm-auto">
            <div className="d-flex gap-1 justify-content-end">
              <Button
                color="success"
                className="add-btn"
                onClick={() => {
                  resetForm();
                  setModal(true);
                }}
                id="create-btn"
              >
                <i className="ri-add-line align-bottom me-1"></i> Nuevo Equipo
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mb-4">
          {loading ? (
            <Loader size={150} />
          ) : (
            <Col xs={12} style={{ position: 'relative', overflow: 'visible' }}>
              <style>{`
                .rdt_Table {
                  overflow: visible !important;
                }
                .rdt_TableBody {
                  overflow: visible !important;
                  min-height: ${currentItems.length <= 3 ? '400px' : 'auto'} !important;
                }
                .rdt_TableCell {
                  overflow: visible !important;
                }
                .rdt_TableRow {
                  overflow: visible !important;
                }
                .page-content {
                  overflow: visible !important;
                }
                .rdt_TableWrapper {
                  overflow: visible !important;
                }
              `}</style>
              <DataTable
                data={currentItems}
                columns={columns}
                noDataComponent={<span className="py-4">Sin resultados</span>}
              />
              <ReactPaginate
                breakLabel="..."
                nextLabel="Sig. >"
                onPageChange={handlePageClick}
                pageRangeDisplayed={10}
                pageCount={pageCount}
                previousLabel="< Ant."
                renderOnZeroPageCount={null}
                className='reactPaginate'
                activeClassName="reactPaginate-active"
              />
            </Col>
          )}
        </Row>
      </Container>

      {/* Modal Crear */}
      <Modal
        isOpen={modal}
        centered
        toggle={() => {
          setModal(false);
          resetForm();
        }}
      >
        <ModalHeader
          className="bg-light p-3"
          id="exampleModalLabel"
        >
          Nuevo Equipo de Venta
        </ModalHeader>
        <div className="tablelist-form">
          <ModalBody style={{ height: "auto" }}>
            <div className="mb-3">
              <label htmlFor="name-field" className="form-label">
                Nombre <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="name-field"
                className="form-control"
                placeholder="Ingresa el nombre del equipo"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="description-field" className="form-label">
                Descripción
              </label>
              <textarea
                id="description-field"
                className="form-control"
                placeholder="Descripción del equipo (opcional)"
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <FormGroup>
              <Label>Estrategia de Asignación</Label>
              <div className="mt-2">
                <FormGroup check className="form-check-inline">
                  <Input
                    type="radio"
                    name="assignmentStrategy"
                    id="round-robin"
                    value="round-robin"
                    checked={assignmentStrategy === "round-robin"}
                    onChange={(e) => setAssignmentStrategy(e.target.value)}
                  />
                  <Label check htmlFor="round-robin">
                    Round Robin (Equitativo)
                  </Label>
                </FormGroup>
                <FormGroup check className="form-check-inline ms-3">
                  <Input
                    type="radio"
                    name="assignmentStrategy"
                    id="percentage"
                    value="percentage"
                    checked={assignmentStrategy === "percentage"}
                    onChange={(e) => setAssignmentStrategy(e.target.value)}
                  />
                  <Label check htmlFor="percentage">
                    Por Porcentajes
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>
            <FormGroup check className="mt-3">
              <Input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <Label check htmlFor="isActive">
                Equipo Activo
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="">
              <button
                type="button"
                className="btn btn-light mx-4"
                onClick={() => {
                  setModal(false);
                  resetForm();
                }}
              >
                Cerrar
              </button>
              <button
                onClick={createTeam}
                className="btn btn-success"
                id="add-btn"
                disabled={!name.trim()}
              >
                Crear
              </button>
            </div>
          </ModalFooter>
        </div>
      </Modal>

      {/* Modal Editar */}
      <Modal
        isOpen={editModal}
        centered
        toggle={() => {
          setEditModal(false);
          setEditingTeam(null);
          resetForm();
        }}
      >
        <ModalHeader
          className="bg-light p-3"
          id="exampleModalLabel"
        >
          Editar Equipo de Venta
        </ModalHeader>
        <div className="tablelist-form">
          <ModalBody style={{ height: "auto" }}>
            <div className="mb-3">
              <label htmlFor="edit-name-field" className="form-label">
                Nombre <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="edit-name-field"
                className="form-control"
                placeholder="Ingresa el nombre del equipo"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="edit-description-field" className="form-label">
                Descripción
              </label>
              <textarea
                id="edit-description-field"
                className="form-control"
                placeholder="Descripción del equipo (opcional)"
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <FormGroup>
              <Label>Estrategia de Asignación</Label>
              <div className="mt-2">
                <FormGroup check className="form-check-inline">
                  <Input
                    type="radio"
                    name="edit-assignmentStrategy"
                    id="edit-round-robin"
                    value="round-robin"
                    checked={assignmentStrategy === "round-robin"}
                    onChange={(e) => setAssignmentStrategy(e.target.value)}
                  />
                  <Label check htmlFor="edit-round-robin">
                    Round Robin (Equitativo)
                  </Label>
                </FormGroup>
                <FormGroup check className="form-check-inline ms-3">
                  <Input
                    type="radio"
                    name="edit-assignmentStrategy"
                    id="edit-percentage"
                    value="percentage"
                    checked={assignmentStrategy === "percentage"}
                    onChange={(e) => setAssignmentStrategy(e.target.value)}
                  />
                  <Label check htmlFor="edit-percentage">
                    Por Porcentajes
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>
            <FormGroup check className="mt-3">
              <Input
                type="checkbox"
                id="edit-isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <Label check htmlFor="edit-isActive">
                Equipo Activo
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="">
              <button
                type="button"
                className="btn btn-light mx-4"
                onClick={() => {
                  setEditModal(false);
                  setEditingTeam(null);
                  resetForm();
                }}
              >
                Cerrar
              </button>
              <button
                onClick={updateTeam}
                className="btn btn-success"
                id="edit-btn"
                disabled={!name.trim()}
              >
                Actualizar
              </button>
            </div>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
};

export default SalesTeams;

